import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Mail, Phone, MapPin, ArrowRight, Send, Globe, Shield, WatchIcon, MessageCircle, MessageCircleCheckIcon, MessageCircleIcon, MessageCircleMore } from 'lucide-react';

const Facebook = ({ size = 24, ...props }) => (
    <svg width={size} height={size} {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
);
const Twitter = ({ size = 24, ...props }) => (
    <svg width={size} height={size} {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" /></svg>
);
const Instagram = ({ size = 24, ...props }) => (
    <svg width={size} height={size} {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
);
const Linkedin = ({ size = 24, ...props }) => (
    <svg width={size} height={size} {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>
);
import API from '../api/axios';
import StatusModal from './StatusModal';
import { BRAND } from '../config/brand';

const Footer = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusModal, setStatusModal] = useState({ isOpen: false, type: 'success', title: '', message: '' });

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await API.post('/newsletter/subscribe', { email });
            setEmail('');
            setStatusModal({
                isOpen: true,
                type: 'success',
                title: 'Identity Registered',
                message: 'Your digital identity has been added to our strategic market registry. Expect premium insights soon.'
            });
        } catch (err) {
            setStatusModal({
                isOpen: true,
                type: 'error',
                title: 'Registration Error',
                message: err.response?.data?.message || 'We encountered an error processing your subscription. Please try again.'
            });
        }
        setLoading(false);
    };
    return (
        <footer className="bg-slate-950 text-white pt-24 pb-12 overflow-hidden relative z-[50]">
            {/* Background Glows */}
            <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary-600/5 rounded-full -ml-64 -mt-64 blur-[120px]"></div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
                    {/* Brand Section */}
                    <div className="space-y-8">
                        <Link to="/" className="flex items-center gap-2 group">
                            <div className="w-12 h-12 overflow-hidden rounded-[0.25rem] flex border-2  items-center justify-center shadow-premium transition-transform group-hover:rotate-12 duration-500">
                                <img src={BRAND.logo} alt={BRAND.name} className="w-full h-full object-cover " />
                            </div>
                            <div className="flex flex-col">
                                <div className="flex items-baseline">
                                    <span className="text-2xl font-black text-white tracking-[0.2em] leading-none">Modern
                                        <span className=" text-secondary">Land</span></span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                    <div className="h-[1px] w-4 bg-secondary"></div>
                                    <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/60 whitespace-nowrap">{BRAND.tagline}</span>
                                    <div className="h-[1px] w-4 bg-secondary"></div>
                                </div>
                            </div>
                        </Link>
                        <p className="text-slate-400 font-medium leading-relaxed">
                            Sri Lanka's premier destination for high-end real estate and land investments. We curate extraordinary lifestyles.
                        </p>
                        <div className="flex items-center gap-3 pt-4">
                            {[
                                { icon: Facebook, href: '#', label: 'Facebook' },
                                { icon: Twitter, href: '#', label: 'Twitter' },
                                { icon: Instagram, href: '#', label: 'Instagram' },
                                { icon: Linkedin, href: '#', label: 'LinkedIn' }
                            ].map((social, i) => (
                                <a key={i} href={social.href} aria-label={`Follow us on ${social.label}`} className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 hover:text-white hover:bg-primary-600 hover:border-primary-600 transition-all duration-300 group shadow-premium">
                                    <social.icon size={15} className="group-hover:scale-110 transition-transform" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Marketplace Links */}
                    <div>
                        <h4 className="text-lg font-black uppercase tracking-[0.2em] mb-8 text-primary-400">Marketplace</h4>
                        <ul className="space-y-4">
                            {[
                                { name: 'About Us', path: '/about' },
                                { name: 'Contact Us', path: '/contact' },
                                { name: 'All Properties', path: '/featured' },
                                { name: 'Strategy Plans', path: '/pricing' }
                            ].map((item) => (
                                <li key={item.name}>
                                    <Link to={item.path} className="text-slate-400 hover:text-white font-bold transition-colors flex items-center gap-2 group">
                                        <ArrowRight size={14} className="text-primary-600 group-hover:translate-x-1 transition-transform" />
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Support */}
                    <div>
                        <h4 className="text-lg font-black uppercase tracking-[0.2em] mb-8 text-primary-400">Support</h4>
                        <ul className="space-y-4">
                            {[
                                { icon: MessageCircleMore, text: BRAND.hotline },
                                { icon: Mail, text: BRAND.hello_mail },
                                { icon: MapPin, text: BRAND.address }
                            ].map((item, i) => (
                                <li key={i} className="flex items-center gap-4 text-slate-400 font-bold group cursor-pointer">
                                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-primary-500 group-hover:bg-primary-600 group-hover:text-white transition-all border border-white/5">
                                        <item.icon size={18} />
                                    </div>
                                    {item.text}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div>
                        <h4 className="text-lg font-black uppercase tracking-[0.2em] mb-8 text-primary-400">Newsletter</h4>
                        <p className="text-slate-400 font-bold text-sm mb-6 uppercase tracking-widest leading-loose">
                            Get the latest listings and market trends.
                        </p>
                        <form onSubmit={handleNewsletterSubmit} className="relative group">
                            <input
                                type="email" required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email address"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white font-bold outline-none focus:border-primary-500 transition-all placeholder:text-slate-600"
                            />
                            <button
                                type="submit" disabled={loading}
                                aria-label="Subscribe to newsletter"
                                className="absolute right-2 top-2 bottom-2 w-12 bg-primary-600 rounded-xl flex items-center justify-center text-white hover:bg-primary-500 transition-all shadow-lg shadow-primary-600/20 disabled:opacity-50"
                            >
                                {loading ? (
                                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                                ) : (
                                    <Send size={18} />
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                <StatusModal
                    isOpen={statusModal.isOpen}
                    type={statusModal.type}
                    title={statusModal.title}
                    message={statusModal.message}
                    onClose={() => setStatusModal(prev => ({ ...prev, isOpen: false }))}
                />

                {/* Bottom Bar */}
                <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8">
                    <p className="text-slate-500 font-black text-xs uppercase tracking-[0.3em]">
                        &copy; 2026 {BRAND.name}. Crafted with excellence.
                    </p>
                    <div className="flex gap-8">
                        <Link to="/privacy-policy" className="text-slate-500 hover:text-white font-black text-[10px] uppercase tracking-[0.2em] transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms-of-service" className="text-slate-500 hover:text-white font-black text-[10px] uppercase tracking-[0.2em] transition-colors">
                            Terms of Service
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
